import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orpc, rpcClient } from 'src/api/rpc/client';
import type {
  CreateAgentScheduleInput,
  UpdateAgentScheduleInput,
} from '@erp/rpc';

/**
 * Hooks over agent-owned run schedules (`agentSchedules.*`). Every mutation
 * invalidates the list, which is keyed by agent definition.
 */

function useInvalidateAgentSchedules() {
  const queryClient = useQueryClient();
  return function invalidateAgentSchedules() {
    return queryClient.invalidateQueries({
      queryKey: orpc.agentSchedules.list.key(),
    });
  };
}

export function useAgentSchedules(agentDefinitionId: string | undefined) {
  return useQuery({
    ...orpc.agentSchedules.list.queryOptions({
      input: { agentDefinitionId: agentDefinitionId ?? '' },
    }),
    enabled: !!agentDefinitionId,
  });
}

export function useCreateAgentSchedule() {
  const invalidateAgentSchedules = useInvalidateAgentSchedules();
  return useMutation({
    mutationFn: (input: CreateAgentScheduleInput) =>
      rpcClient.agentSchedules.create(input),
    onSuccess: invalidateAgentSchedules,
  });
}

export function useUpdateAgentSchedule() {
  const invalidateAgentSchedules = useInvalidateAgentSchedules();
  return useMutation({
    mutationFn: (input: UpdateAgentScheduleInput) =>
      rpcClient.agentSchedules.update(input),
    onSuccess: invalidateAgentSchedules,
  });
}

export function useDeleteAgentSchedule() {
  const invalidateAgentSchedules = useInvalidateAgentSchedules();
  return useMutation({
    mutationFn: ({ id }: { id: string }) =>
      rpcClient.agentSchedules.delete({ id }),
    onSuccess: invalidateAgentSchedules,
  });
}
